import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import QuoteRequestDialog from '../components/QuoteRequestDialog';
import ServiceDetailDialog from '../components/ServiceDetailDialog';
import {
  RequestQuote as QuoteIcon,
  LocalGasStation as GasIcon,
  Build as BuildIcon,
  Receipt as ReceiptIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircleOutline as CheckIcon,
  WhatsApp as WhatsAppIcon,
  Verified as VerifiedIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';

/* ─── Palette ─── */
const palette = {
  navy: '#0f1b2d',
  navyLight: '#162338',
  primary: '#1e56a0',
  primaryLight: '#2b6cb0',
  accent: '#06b6d4',
  highlight: '#eab308',
  white: '#ffffff',
  grayLight: '#f8fafc',
  gray100: '#f1f5f9',
  gray200: '#e2e8f0',
  gray400: '#94a3b8',
  gray600: '#475569',
  gray800: '#1e293b',
};

const services = [
  {
    icon: <GasIcon sx={{ fontSize: 40 }} />,
    title: 'Distribuzione GPL',
    description: 'Fornitura e distribuzione di GPL per uso domestico, commerciale e industriale con consegne puntuali e sicure.',
    color: palette.primary,
    image: '/services/distribuzione-gpl.png',
    longDescription: 'GPL Service garantisce un servizio di distribuzione GPL capillare e affidabile in Abruzzo e nel Centro Italia. Grazie alla nostra flotta di autobotti certificate e a un sistema logistico efficiente, assicuriamo rifornimenti puntuali per abitazioni private, condomini, attività commerciali e impianti industriali.',
    details: [
      'Consegne programmate con calendario personalizzato',
      'Rifornimento automatico basato sui consumi stimati',
      'Autobotti certificate e mezzi di ultima generazione',
      'Copertura capillare in Abruzzo e Centro Italia',
      'Prezzi competitivi e trasparenti senza costi nascosti',
      'Servizio di emergenza per rifornimenti urgenti',
    ],
  },
  {
    icon: <BuildIcon sx={{ fontSize: 40 }} />,
    title: 'Manutenzione Impianti',
    description: 'Installazione, manutenzione ordinaria e straordinaria di impianti GPL a norma di legge.',
    color: palette.accent,
    image: '/services/manutenzione.png',
    longDescription: 'Il nostro team di tecnici specializzati si occupa dell\'intero ciclo di vita dei tuoi impianti GPL: dall\'installazione iniziale alla manutenzione programmata, fino agli interventi straordinari. Ogni operazione viene svolta nel pieno rispetto delle normative vigenti per garantire sicurezza e massima efficienza.',
    details: [
      'Installazione serbatoi interrati e fuori terra',
      'Manutenzione ordinaria programmata e periodica',
      'Interventi straordinari e riparazioni urgenti',
      'Adeguamento impianti alle normative vigenti',
      'Tecnici certificati e costantemente aggiornati',
      'Rilascio documentazione e certificazioni a norma',
    ],
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Verifiche e Collaudi',
    description: 'Verifiche periodiche, collaudi e certificazioni di conformità per impianti e serbatoi GPL.',
    color: '#8b5cf6',
    image: '/services/verifiche-collaudi.png',
    longDescription: 'Eseguiamo tutte le verifiche e i collaudi previsti dalla legge per i serbatoi e gli impianti GPL, garantendo la piena conformità normativa. Il nostro servizio include la gestione completa dell\'iter burocratico e il coordinamento con gli enti preposti come INAIL e ASL.',
    details: [
      'Verifiche decennali dei serbatoi GPL',
      'Collaudi INAIL e verifiche di integrità',
      'Prove di tenuta e controlli di sicurezza',
      'Certificazioni di conformità impianti',
      'Gestione completa della documentazione',
      'Coordinamento con enti ispettivi (ASL, INAIL)',
    ],
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Lettura Contatori',
    description: 'Servizio di telelettura e gestione contatori per un monitoraggio preciso dei consumi.',
    color: '#f59e0b',
    image: '/services/lettura-contatori.png',
    longDescription: 'Il nostro sistema di telelettura permette il monitoraggio remoto e in tempo reale dei consumi GPL. Questo si traduce in fatturazioni sempre precise basate sui consumi effettivi, nella possibilità di individuare tempestivamente anomalie e in un servizio di rifornimento ottimizzato sulle tue reali necessità.',
    details: [
      'Telelettura remota con trasmissione dati automatica',
      'Monitoraggio consumi in tempo reale',
      'Storico letture consultabile per analisi consumi',
      'Alert automatici in caso di anomalie o consumi insoliti',
      'Fatturazione sempre basata su consumi reali',
      'Installazione e manutenzione dei dispositivi inclusa',
    ],
  },
  {
    icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
    title: 'Fatturazione Trasparente',
    description: 'Gestione completa della fatturazione con bollette chiare, dettagliate e puntuali.',
    color: '#10b981',
    image: '/services/fatturazione.png',
    longDescription: 'Crediamo nella massima trasparenza verso i nostri clienti. Le nostre fatture sono chiare, dettagliate e facilmente comprensibili, con l\'indicazione precisa dei consumi, dei prezzi unitari e di tutte le voci di costo. Offriamo anche diverse modalità di pagamento per la massima comodità.',
    details: [
      'Fatture dettagliate con indicazione precisa dei consumi',
      'Invio puntuale tramite email o posta ordinaria',
      'Storico consumi e pagamenti sempre consultabile',
      'Piani di pagamento rateali personalizzabili',
      'Domiciliazione bancaria e pagamento online',
      'Assistenza dedicata per chiarimenti sulle bollette',
    ],
  },
  {
    icon: <SupportIcon sx={{ fontSize: 40 }} />,
    title: 'Assistenza Clienti',
    description: 'Supporto dedicato e personale qualificato sempre a disposizione per ogni esigenza.',
    color: '#ef4444',
    image: '/services/assistenza.png',
    longDescription: 'Il nostro servizio clienti è il punto di riferimento per ogni tua esigenza legata alla fornitura GPL. Personale qualificato e cortese è sempre a disposizione per rispondere alle tue domande, gestire le richieste e garantirti un\'esperienza senza pensieri.',
    details: [
      'Supporto telefonico con operatori dedicati',
      'Reperibilità per emergenze e situazioni urgenti',
      'Personale qualificato e costantemente formato',
      'Tempi di risposta rapidi e risolutivi',
      'Gestione reclami e segnalazioni con follow-up',
      'Consulenza personalizzata per ogni esigenza',
    ],
  },
];

const trustBadges = [
  { icon: <VerifiedIcon />, label: '100% Certificati' },
  { icon: <EngineeringIcon />, label: 'Tecnici Specializzati' },
  { icon: <LocationIcon />, label: 'Abruzzo e Centro Italia' },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.grayLight }}>
      {/* ─── Top Bar ─── */}
      <Box sx={{
        bgcolor: palette.navy,
        color: palette.gray400,
        py: 0.75,
        px: 2,
        fontSize: '0.8rem',
        display: { xs: 'none', md: 'block' },
      }}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" component="a" href="tel:+393299028484" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}>
                329 9028484
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmailIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption" component="a" href="mailto:utenze@gplservice.srl" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}>
                utenze@gplservice.srl
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ScheduleIcon sx={{ fontSize: 14 }} />
              <Typography variant="caption">Lun–Ven: 8–18 | Sab: 8–12</Typography>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* ─── Navbar ─── */}
      <AppBar position="sticky" elevation={0} sx={{
        bgcolor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(20px)',
        color: 'text.primary',
        borderBottom: `1px solid ${palette.gray200}`,
      }}>
        <Toolbar sx={{ justifyContent: 'space-between', maxWidth: 'lg', width: '100%', mx: 'auto', minHeight: { xs: 64, md: 72 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/logo192.png"
              alt="GPL Service"
              sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 } }}
            />
            {!isMobile && (
              <Typography variant="h6" fontWeight="800" sx={{ color: palette.navy }}>
                GPL Service
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              startIcon={<PhoneIcon />}
              href="tel:+393299028484"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                color: palette.gray600,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              329 9028484
            </Button>
            <Button
              variant="contained"
              startIcon={<QuoteIcon />}
              onClick={() => setQuoteOpen(true)}
              sx={{
                borderRadius: 2.5,
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                bgcolor: palette.primary,
                '&:hover': { bgcolor: palette.primaryLight },
              }}
            >
              Richiedi Preventivo
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* ─── Hero ─── */}
      <Box
        sx={{
          background: `linear-gradient(160deg, ${palette.navy} 0%, ${palette.navyLight} 40%, #1a365d 100%)`,
          color: 'white',
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 14 },
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute', top: -100, right: -100,
          width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.accent}15, transparent 70%)`,
        }} />
        <Box sx={{
          position: 'absolute', bottom: -80, left: -60,
          width: 300, height: 300, borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.primary}20, transparent 70%)`,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={7}>
              <Chip
                label="Distribuzione e Assistenza GPL"
                size="small"
                sx={{
                  bgcolor: 'rgba(6,182,212,0.15)',
                  color: palette.accent,
                  fontWeight: 600,
                  mb: 3,
                  border: `1px solid rgba(6,182,212,0.3)`,
                }}
              />
              <Typography
                variant={isMobile ? 'h3' : 'h2'}
                fontWeight="800"
                sx={{ mb: 2, lineHeight: 1.15 }}
              >
                Il tuo partner{' '}
                <Box component="span" sx={{
                  background: `linear-gradient(135deg, ${palette.accent}, ${palette.highlight})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}>
                  di fiducia
                </Box>{' '}
                per il GPL
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.75, fontWeight: 400, maxWidth: 520, lineHeight: 1.6 }}
              >
                Installazione, manutenzione e assistenza impianti GPL per privati, condomini e aziende in Abruzzo e Centro Italia.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  href="tel:+393299028484"
                  sx={{
                    bgcolor: palette.accent,
                    color: palette.navy,
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2.5,
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#0891b2' },
                  }}
                >
                  Contattaci Ora
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<QuoteIcon />}
                  onClick={() => setQuoteOpen(true)}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.3)',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2.5,
                    textTransform: 'none',
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  Preventivo Online
                </Button>
              </Stack>

              {/* Trust badges */}
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {trustBadges.map((badge, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ color: palette.accent, display: 'flex' }}>
                      {badge.icon}
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.8, fontWeight: 500 }}>
                      {badge.label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Right column — logo showcase */}
            {!isTablet && (
              <Grid item md={5} sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
                <Box sx={{
                  position: 'absolute', width: 320, height: 320, borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.05)',
                  animation: 'pulse 3s ease-in-out infinite',
                  '@keyframes pulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
                }} />
                <Box sx={{
                  width: 280, height: 280,
                  bgcolor: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 6,
                  p: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }}>
                  <Box
                    component="img"
                    src="/logo192.png"
                    alt="GPL Service"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.3))' }}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>

        {/* Bottom curve */}
        <Box sx={{
          position: 'absolute', bottom: -1, left: 0, right: 0,
          height: 60,
          background: palette.grayLight,
          borderRadius: '100% 100% 0 0 / 100% 100% 0 0',
        }} />
      </Box>



      {/* ─── Servizi ─── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, sm: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip
            label="I Nostri Servizi"
            size="small"
            sx={{
              bgcolor: `${palette.primary}12`,
              color: palette.primary,
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="800" gutterBottom color={palette.gray800}>
            Soluzioni complete per il GPL
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
            Da oltre 20 anni al servizio del territorio con professionalità, sicurezza e affidabilità.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {services.map((service, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                elevation={0}
                onClick={() => setSelectedService(index)}
                sx={{
                  height: '100%',
                  border: '1px solid',
                  borderColor: palette.gray200,
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                    borderColor: service.color,
                    transform: 'translateY(-6px)',
                    '& .service-img': { transform: 'scale(1.05)' },
                    '&::before': { opacity: 1 },
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: 3,
                    background: `linear-gradient(90deg, ${service.color}, ${service.color}80)`,
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    zIndex: 1,
                  },
                }}
              >
                <Box sx={{ height: 180, overflow: 'hidden', position: 'relative' }}>
                  <Box
                    className="service-img"
                    component="img"
                    src={service.image}
                    alt={service.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.4s ease',
                    }}
                  />
                </Box>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Box sx={{
                      width: 44, height: 44,
                      borderRadius: 2,
                      bgcolor: `${service.color}12`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: service.color,
                      flexShrink: 0,
                    }}>
                      {React.cloneElement(service.icon, { sx: { fontSize: 24 } })}
                    </Box>
                    <Typography variant="h6" fontWeight="700">
                      {service.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ─── Perché sceglierci ─── */}
      <Box sx={{ bgcolor: palette.gray100, py: { xs: 8, sm: 12 }, px: 2 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Chip label="Perché GPL Service" size="small" sx={{ bgcolor: `${palette.accent}15`, color: palette.accent, fontWeight: 700, mb: 2 }} />
              <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="800" gutterBottom color={palette.gray800}>
                Il partner che si prende cura dei tuoi impianti
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                Operiamo in Abruzzo e nel Centro Italia con tecnici certificati e attrezzature all'avanguardia per garantire la massima sicurezza ed efficienza.
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                {[
                  { title: 'Tecnici Certificati', desc: 'Personale qualificato e costantemente aggiornato' },
                  { title: 'Rapidità di Intervento', desc: 'Tempi di risposta rapidi per ogni esigenza' },
                  { title: 'Conformità Normativa', desc: 'Impianti a norma con certificazioni ufficiali' },
                  { title: 'Assistenza Dedicata', desc: 'Un referente dedicato per ogni esigenza' },
                ].map((item, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box sx={{
                      bgcolor: 'white',
                      p: 3,
                      borderRadius: 3,
                      border: `1px solid ${palette.gray200}`,
                      height: '100%',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CheckIcon sx={{ color: palette.accent, fontSize: 22 }} />
                        <Typography variant="subtitle1" fontWeight="700">{item.title}</Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">{item.desc}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─── CTA Section ─── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${palette.navy} 0%, ${palette.primary} 100%)`,
          color: 'white',
          py: { xs: 8, sm: 10 },
          textAlign: 'center',
          px: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.03), transparent 70%)',
        }} />
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="800" gutterBottom>
            Hai bisogno di assistenza?
          </Typography>
          <Typography variant="body1" sx={{ mb: 5, opacity: 0.85, lineHeight: 1.8 }}>
            Contattaci per un preventivo gratuito o per qualsiasi informazione sui nostri servizi. Siamo a tua disposizione.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<PhoneIcon />}
              href="tel:+393299028484"
              sx={{
                bgcolor: palette.accent,
                color: palette.navy,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: '#0891b2' },
              }}
            >
              Chiama Ora
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<WhatsAppIcon />}
              href="https://wa.me/393299028484"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                bgcolor: '#25d366',
                color: 'white',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: '#22c55e' },
              }}
            >
              WhatsApp
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* ─── Footer ─── */}
      <Box component="footer" sx={{ bgcolor: palette.navy, color: 'rgba(255,255,255,0.7)', pt: 8, pb: 4, px: 2 }}>
        {/* Top accent line */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${palette.primary}, ${palette.accent}, ${palette.highlight})` }} />
        <Container maxWidth="lg">
          <Grid container spacing={5}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box component="img" src="/logo192.png" alt="GPL Service" sx={{ width: 48, height: 48, filter: 'brightness(0) invert(1)' }} />
                <Typography variant="h6" fontWeight="800" sx={{ color: 'white' }}>GPL Service</Typography>
              </Box>
              <Typography variant="body2" sx={{ lineHeight: 1.8, mb: 2 }}>
                Il tuo partner di fiducia per l'installazione, la manutenzione e l'assistenza di impianti GPL in Abruzzo e nel Centro Italia.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: 'white', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                Contatti
              </Typography>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, color: palette.accent }} />
                  <Typography variant="body2" component="a" href="tel:+393299028484" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}>329 9028484</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: palette.accent }} />
                  <Typography variant="body2" component="a" href="mailto:utenze@gplservice.srl" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}>utenze@gplservice.srl</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LocationIcon sx={{ fontSize: 18, color: palette.accent, mt: 0.25 }} />
                  <Typography variant="body2">Via Oslavia, 21 – 67051 Avezzano (AQ)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ScheduleIcon sx={{ fontSize: 18, color: palette.accent }} />
                  <Typography variant="body2">Lun–Ven: 8–18 | Sab: 8–12</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="subtitle2" fontWeight="700" sx={{ color: 'white', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                Accesso Rapido
              </Typography>
              <Stack spacing={1}>
                <Typography
                  variant="body2"
                  sx={{ cursor: 'pointer', '&:hover': { color: palette.accent } }}
                  onClick={() => navigate('/login')}
                >
                  Area Riservata
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href="tel:+393299028484"
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}
                >
                  Contattaci
                </Typography>
                <Typography
                  variant="body2"
                  component="a"
                  href="https://wa.me/393299028484"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: palette.accent } }}
                >
                  WhatsApp
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 4 }} />

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} GPL Service S.r.l. — P.IVA: 02157600665 — Tutti i diritti riservati
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ─── Service Detail Dialog ─── */}
      <ServiceDetailDialog
        service={selectedService !== null ? services[selectedService] : null}
        open={selectedService !== null}
        onClose={() => setSelectedService(null)}
        onRequestQuote={() => { setSelectedService(null); setQuoteOpen(true); }}
      />

      {/* ─── Quote Request Dialog ─── */}
      <QuoteRequestDialog open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </Box>
  );
};

export default LandingPage;
